<template>
  <div class="workload-stats">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>工作量统计</span>
          <el-select v-model="selectedSemesterId" placeholder="选择学期" @change="fetchStats">
            <el-option v-for="s in semesters" :key="s.id" :label="s.name" :value="s.id" />
          </el-select>
        </div>
      </template>

      <el-alert
        title="时长计算规则：105分钟（1.75小时）按2小时计算；巡考不计入时长。"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 16px"
      />

      <el-table :data="stats" v-loading="loading" stripe>
        <el-table-column type="index" label="#" width="60" />
        <el-table-column prop="teacher_name" label="教师姓名" width="120" />
        <el-table-column prop="total_sessions" label="监考场次" width="120">
          <template #default="{ row }">
            <span class="stat-value">{{ row.total_sessions }}</span> 场
          </template>
        </el-table-column>
        <el-table-column prop="total_hours" label="监考时长" width="120">
          <template #default="{ row }">
            <span class="stat-value">{{ row.total_hours.toFixed(2) }}</span> 小时
          </template>
        </el-table-column>
        <el-table-column label="工作量分布" min-width="300">
          <template #default="{ row }">
            <el-progress :percentage="getPercentage(row.total_hours)" :stroke-width="18"
              :color="getProgressColor(row.total_hours)">
              <span class="progress-text">{{ row.total_hours.toFixed(2) }} 小时</span>
            </el-progress>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="!loading && stats.length === 0" class="empty-state">
        <el-empty description="暂无统计数据" />
      </div>
    </el-card>

    <!-- Summary Card -->
    <el-row :gutter="20" style="margin-top: 20px" v-if="stats.length > 0">
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #667eea, #764ba2)">
            <el-icon :size="28">
              <User />
            </el-icon>
          </div>
          <div class="stat-content">
            <div class="stat-label">参与监考教师</div>
            <div class="stat-number">{{ stats.length }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #f093fb, #f5576c)">
            <el-icon :size="28">
              <Calendar />
            </el-icon>
          </div>
          <div class="stat-content">
            <div class="stat-label">总监考场次</div>
            <div class="stat-number">{{ totalSessions }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #4facfe, #00f2fe)">
            <el-icon :size="28">
              <Clock />
            </el-icon>
          </div>
          <div class="stat-content">
            <div class="stat-label">总监考时长</div>
            <div class="stat-number">{{ totalHours.toFixed(2) }}h</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card class="stat-card">
          <div class="stat-icon" style="background: linear-gradient(135deg, #43e97b, #38f9d7)">
            <el-icon :size="28">
              <TrendCharts />
            </el-icon>
          </div>
          <div class="stat-content">
            <div class="stat-label">人均时长</div>
            <div class="stat-number">{{ avgHours.toFixed(2) }}h</div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { User, Calendar, Clock, TrendCharts } from '@element-plus/icons-vue'
import { semesterApi, scheduleApi } from '@/api'

const semesters = ref([])
const selectedSemesterId = ref(null)
const stats = ref([])
const loading = ref(false)

const totalSessions = computed(() =>
  stats.value.reduce((sum, s) => sum + s.total_sessions, 0)
)

const totalHours = computed(() =>
  stats.value.reduce((sum, s) => sum + s.total_hours, 0)
)

const avgHours = computed(() =>
  stats.value.length > 0 ? totalHours.value / stats.value.length : 0
)

const maxHours = computed(() =>
  Math.max(...stats.value.map(s => s.total_hours), 1)
)

const getPercentage = (hours) => {
  return Math.round((hours / maxHours.value) * 100)
}

const getProgressColor = (hours) => {
  const avg = avgHours.value
  if (hours > avg * 1.3) return '#f56c6c'  // Too high
  if (hours < avg * 0.7) return '#e6a23c'  // Too low
  return '#67c23a'  // Normal
}

const fetchSemesters = async () => {
  try {
    semesters.value = await semesterApi.getAll()
    if (semesters.value.length > 0) {
      const active = semesters.value.find(s => s.is_active)
      selectedSemesterId.value = active?.id || semesters.value[0].id
      fetchStats()
    }
  } catch (e) {
    console.error(e)
  }
}

const fetchStats = async () => {
  if (!selectedSemesterId.value) return
  loading.value = true
  try {
    stats.value = await scheduleApi.getSemesterStats(selectedSemesterId.value)
    // Sort by hours descending
    stats.value.sort((a, b) => b.total_hours - a.total_hours)
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

onMounted(fetchSemesters)
</script>

<style scoped>
.workload-stats {
  max-width: 1200px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header span {
  font-size: 16px;
  font-weight: 500;
}

.stat-value {
  font-weight: 600;
  color: #667eea;
}

.progress-text {
  font-size: 12px;
  color: #e2e8f0;
}

.empty-state {
  padding: 40px 0;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px !important;
}

.stat-card :deep(.el-card__body) {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.stat-content {
  flex: 1;
}

.stat-label {
  color: #a0aec0;
  font-size: 13px;
  margin-bottom: 4px;
}

.stat-number {
  color: #e2e8f0;
  font-size: 24px;
  font-weight: 600;
}
</style>
